/**
 * Recording a πρόσθετη πράξη — a mid-term amendment.
 *
 * The backend has supported these from the first pass (endpoint, schema, premium history,
 * instalment rebuild) and the policy page listed them, but there was no way to create one:
 * a coverage change mid-term is an everyday act in an agency, and it had to be done by
 * editing the policy, which silently rewrites history instead of recording the amendment.
 *
 * Hand-built rather than `AutoForm` for one reason: the operator needs to see what the
 * change does to the policy *before* committing it. The insurer tells them "the premium
 * becomes €720"; the delta, the direction and the effect on the instalment schedule are
 * derived here and shown live, the same way the renewal dialog previews its transition.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import {
  createEndorsementSchema,
  formatPolicyRef,
  normaliseAmountInput,
  today,
  type PolicyDto,
} from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEnumOptions } from '@/components/common/enum-badge';
import { RequiresConnection } from '@/components/offline/offline-indicator';
import { ApiRequestError } from '@/lib/api/client';
import { useCreateEndorsement } from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function EndorsementFormDialog({
  policy,
  open,
  onOpenChange,
}: {
  policy: PolicyDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, money } = useI18n();
  const typeOptions = useEnumOptions('endorsementType');
  const create = useCreateEndorsement(policy.id);

  const [endorsementType, setEndorsementType] = React.useState('coverage_change');
  const [effectiveDate, setEffectiveDate] = React.useState(() => today());
  const [description, setDescription] = React.useState('');
  const [endorsementNumber, setEndorsementNumber] = React.useState('');
  const [newPremium, setNewPremium] = React.useState('');
  const [newNetPremium, setNewNetPremium] = React.useState('');

  // A fresh form each time it opens — a dialog that remembers the last amendment invites
  // recording it twice.
  React.useEffect(() => {
    if (open) {
      setEndorsementType('coverage_change');
      setEffectiveDate(today());
      setDescription('');
      setEndorsementNumber('');
      setNewPremium('');
      setNewNetPremium('');
    }
  }, [open]);

  const current = Number(policy.premiumAmount);
  /*
   * `.replace(',', '.')` said the right thing and said it once: it swapped the first comma and
   * left «1.234,56» as «1.234.56», which is NaN. The shared reader knows that a comma means the
   * dots before it were thousands — and it is the same one the renewal dialog uses, so the two
   * screens cannot disagree about what the office just typed.
   */
  const target = newPremium.trim() === '' ? current : Number(normaliseAmountInput(newPremium) ?? NaN);
  const premiumValid = Number.isFinite(target) && target >= 0;
  const delta = premiumValid ? target - current : 0;
  const changesPremium = premiumValid && Math.abs(delta) >= 0.005;

  /*
   * The ΚΑΘΑΡΑ are asked for separately and never derived from the gross change.
   *
   * A πρόσθετη πράξη that adds 40 € to what the client pays adds some smaller amount to the net —
   * the split depends on the carrier's tax and levy rates for that cover, which this system does
   * not hold. So an amendment that moves the premium without a new net figure leaves the
   * commission on the old base, and the hint below says so rather than letting it pass silently.
   */
  const currentNet = policy.netPremiumAmount === null ? null : Number(policy.netPremiumAmount);
  const netTarget =
    newNetPremium.trim() === '' ? currentNet : Number(normaliseAmountInput(newNetPremium) ?? NaN);
  const netValid = netTarget === null || (Number.isFinite(netTarget) && netTarget >= 0 && netTarget <= target);
  const changesNet =
    netValid && newNetPremium.trim() !== '' && (currentNet === null || Math.abs(netTarget! - currentNet) >= 0.005);
  const netLeftBehind = changesPremium && !changesNet;

  const canSubmit =
    description.trim().length >= 3 && premiumValid && netValid && Boolean(effectiveDate);

  const submit = async () => {
    try {
      /*
       * Both `premiumDelta` and `newPremiumAmount` are sent. The server prefers the
       * explicit amount and falls back to shifting the current premium by the delta —
       * sending both means the record carries the figure the operator actually typed *and*
       * the movement it represents, so the endorsement history reads correctly without a
       * later subtraction.
       */
      const payload = createEndorsementSchema.parse({
        endorsementType,
        effectiveDate,
        description: description.trim(),
        endorsementNumber: endorsementNumber.trim() || null,
        premiumDelta: changesPremium ? delta.toFixed(2) : '0.00',
        newPremiumAmount: changesPremium ? target.toFixed(2) : null,
        newNetPremiumAmount: changesNet ? netTarget!.toFixed(2) : null,
      });

      await create.mutateAsync(payload);
      toast.success(t('endorsement.saved'));
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : t('error.generic'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('endorsement.new')}</DialogTitle>
          <DialogDescription>
            <span className="tabular">{formatPolicyRef(policy.policyNumber, policy.plates)}</span> ·{' '}
            {policy.clientName}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="endorsement-type">{t('endorsement.type')}</Label>
              <Select value={endorsementType} onValueChange={setEndorsementType}>
                <SelectTrigger id="endorsement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="endorsement-date">{t('endorsement.effectiveDate')}</Label>
              <Input
                id="endorsement-date"
                type="date"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="endorsement-description">{t('endorsement.description')}</Label>
            <Textarea
              id="endorsement-description"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              autoFocus
            />
            <p className="text-muted-soft text-xs">{t('endorsement.descriptionHint')}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="endorsement-premium">{t('endorsement.newPremium')}</Label>
              <Input
                id="endorsement-premium"
                inputMode="decimal"
                className="tabular"
                placeholder={current.toFixed(2)}
                value={newPremium}
                onChange={(event) => setNewPremium(event.target.value)}
                aria-invalid={!premiumValid}
              />
              <p className="text-muted-soft text-xs">{t('endorsement.newPremiumHint')}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="endorsement-net-premium">{t('policy.netPremium')}</Label>
              <Input
                id="endorsement-net-premium"
                inputMode="decimal"
                className="tabular"
                placeholder={currentNet === null ? '—' : currentNet.toFixed(2)}
                value={newNetPremium}
                onChange={(event) => setNewNetPremium(event.target.value)}
                aria-invalid={!netValid}
              />
              <p className="text-muted-soft text-xs">{t('policy.netPremiumHint')}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="endorsement-number">{t('endorsement.number')}</Label>
              <Input
                id="endorsement-number"
                className="tabular"
                value={endorsementNumber}
                onChange={(event) => setEndorsementNumber(event.target.value)}
              />
            </div>
          </div>

          {/* The preview: what this will do, before it is done. */}
          <div className="border-border bg-muted/40 space-y-2 rounded-xl border p-3.5">
            <p className="text-muted-soft text-[11px] font-semibold tracking-[0.07em] uppercase">
              {t('endorsement.preview')}
            </p>

            {changesPremium ? (
              <>
                <div className="flex flex-wrap items-center gap-2 text-[13.5px]">
                  <span className="text-muted-foreground tabular">{money(policy.premiumAmount)}</span>
                  <ArrowRight className="text-muted-soft size-3.5" />
                  <span className="tabular font-semibold">{money(target.toFixed(2))}</span>
                  <span
                    className={cn('tabular text-[13px] font-medium', delta < 0 ? 'text-good' : 'text-serious')}
                  >
                    {delta > 0 ? '+' : ''}
                    {money(delta.toFixed(2))}
                  </span>
                </div>
                <p className="text-muted-soft text-xs">{t('endorsement.installmentsNote')}</p>
                {netLeftBehind && (
                  <p className="text-serious text-xs">{t('endorsement.netUnchanged')}</p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground text-[13.5px]">
                {premiumValid ? t('endorsement.noChange') : t('error.invalidNumber')}
              </p>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          {/* A mid-term amendment recalculates money and rebuilds the instalment
              schedule — the same reason renewal is never queued offline. */}
          <RequiresConnection reasonKey="offline.blocked.contract">
            <Button disabled={!canSubmit} loading={create.isPending} onClick={() => void submit()}>
              {t('action.save')}
            </Button>
          </RequiresConnection>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
